/**
 * @description Component for email validation of a user account.
 */

import { useEffect, useRef, useState } from "react";
import { ArrowRight, AlertCircle, BadgeCheck, Mail } from "lucide-react";
import api from "@/utils/axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

interface VerifyEmailProps {
  navigate?: (page: string) => void;
}

export default function VerifyEmail({ navigate }: VerifyEmailProps) {
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const hasFetched = useRef(false);

  const pageStyle = {
    background:
      "linear-gradient(135deg, color-mix(in srgb, var(--primary) 10%, var(--background)), color-mix(in srgb, var(--surface) 80%, var(--background)))",
  } as const;
  const iconBadgeStyle = {
    backgroundColor:
      "color-mix(in srgb, var(--primary) 18%, var(--background))",
  } as const;
  const iconStyle = {
    color: "color-mix(in srgb, var(--primary) 82%, var(--foreground))",
  } as const;
  const successPanelStyle = {
    backgroundColor:
      "color-mix(in srgb, var(--primary) 8%, var(--surface))",
    borderColor:
      "color-mix(in srgb, var(--primary) 26%, var(--background))",
  } as const;
  const errorPanelStyle = {
    backgroundColor:
      "color-mix(in srgb, var(--accent) 8%, var(--surface))",
    borderColor:
      "color-mix(in srgb, var(--accent) 25%, var(--background))",
  } as const;
  const mutedTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 72%, var(--background))",
  } as const;
  const strongPrimaryStyle = {
    color: "color-mix(in srgb, var(--primary) 82%, var(--foreground))",
  } as const;
  const dangerTextStyle = {
    color: "color-mix(in srgb, var(--accent) 78%, var(--foreground))",
  } as const;

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const token = searchParams.get("token");

    if (!token) {
      setStatus("error");
      return;
    }

    if (hasFetched.current) return;
    hasFetched.current = true;

    const verifyToken = async () => {
      try {
        await api.post("/auth/verify-email", { token });
        setStatus("success");
      } catch (error) {
        setStatus("error");
      }
    };

    verifyToken();
  }, []);

  const goToLogin = () => {
    if (navigate) {
      navigate("login");
      return;
    }
    window.location.href = "/login";
  };

  return (
    <div
      className="flex min-h-screen items-center justify-center p-4"
      style={pageStyle}
    >
      <Card className="w-full max-w-md overflow-hidden border border-border/60 shadow-xl backdrop-blur-sm">
        <CardHeader className="space-y-4 pb-4 text-center">
          <div
            className="mx-auto flex h-16 w-16 items-center justify-center rounded-full"
            style={iconBadgeStyle}
          >
            <Mail className="h-8 w-8" style={iconStyle} />
          </div>
          <CardTitle className="card-title-text" style={strongPrimaryStyle}>
            E-Mail Verifizierung
          </CardTitle>
          <CardDescription className="card-description-text" style={mutedTextStyle}>
            Wir prüfen deinen Bestätigungslink und bringen dich danach direkt zum Login.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {status === "loading" && (
            <div
              className="rounded-2xl border px-5 py-6 text-center"
              style={successPanelStyle}
            >
              <p className="font-medium" style={strongPrimaryStyle}>
                Verifiziere deinen Link...
              </p>
              <p className="mt-2 text-sm" style={mutedTextStyle}>
                Bitte warte einen Moment.
              </p>
            </div>
          )}

          {status === "success" && (
            <div
              className="space-y-4 rounded-2xl border px-5 py-6 text-center"
              style={successPanelStyle}
            >
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[color-mix(in_srgb,var(--primary)_14%,var(--background))]">
                <BadgeCheck className="h-7 w-7" style={iconStyle} />
              </div>
              <p className="text-lg font-semibold" style={strongPrimaryStyle}>
                Dein Account wurde erfolgreich aktiviert.
              </p>
              <p className="text-sm" style={mutedTextStyle}>
                Du kannst dich jetzt anmelden und WebKurator weiter nutzen.
              </p>
              <Button onClick={goToLogin} className="w-full gap-2">
                Zum Login
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          )}

          {status === "error" && (
            <div
              className="space-y-4 rounded-2xl border px-5 py-6 text-center"
              style={errorPanelStyle}
            >
              <AlertCircle className="mx-auto h-10 w-10" style={dangerTextStyle} />
              <p className="text-lg font-semibold" style={dangerTextStyle}>
                Verifizierung fehlgeschlagen.
              </p>
              <p className="text-sm" style={mutedTextStyle}>
                Der Link ist ungültig oder abgelaufen.
              </p>
              <Button variant="outline" onClick={goToLogin} className="w-full gap-2">
                Zurück zum Login
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
